import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import { translatedMessage } from '../../service/LanguageService';
import useAuth from '../../hooks/useAuth';
import DataTableUtils from '../../utilities/DataTableUtils';
import DeleteModalComponent from '../DeleteModalComponent';
import AnswerEditDialog from './AnswerEditDialog';
import { CompetitionService } from '../../service/CompetitionService';
import DOMPurify from 'dompurify';
import { QuestionType } from './QuestionType';

const AnswerListComponent = (props) => {
    const [answers, setAnswers] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);

    const [filters] = useState({
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.IN },
        active: { value: null, matchMode: FilterMatchMode.IN }
    });

    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();

    const competitionService = useMemo(() => new CompetitionService(), []);

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    useEffect(() => {
        setIsLoading(true);
        setAnswers(props.answers);
        setIsLoading(false);

        let _selectedAnswer = props.answers.find((_p) => _p.id === selectedAnswer?.id);
        setSelectedAnswer(_selectedAnswer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.answers, props.canEdit, competitionService]);

    const showNewDialog = () => {
        setSelectedAnswer(null);
        setEditDialogVisible(true);
    };

    const showEditDialog = (answer) => {
        setSelectedAnswer(answer);
        setEditDialogVisible(true);
    };

    const hideEditDialog = () => {
        setSelectedAnswer(null);
        setEditDialogVisible(false);
    };

    const showDeleteDialog = (answer) => {
        setSelectedAnswer(answer);
        setDeleteDialogVisible(true);
    };

    const hideDeleteDialog = () => {
        setDeleteDialogVisible(false);
    };

    const deleteAnswer = () => {
        competitionService
            .deleteQuestionAnswer(selectedAnswer.id)
            .then(() => {
                refreshData();
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteDialogVisible(false);
                setSelectedAnswer(null);
            });
    };

    const afterSaveQuestionAnswer = async (savedQuestionAnswer, isNew) => {
        if (isNew) {
            setSelectedAnswer(savedQuestionAnswer);
            setEditDialogVisible(true);
        }
        await refreshData();
    };

    const refreshData = async () => {
        competitionService
            .getCompetitionSubjects(props.competition?.id)
            .then((_subjects) => {
                let _proposals = _subjects.map((subject) => subject.proposals).flat();
                let _selectedProposal = _proposals.find((_p) => _p.id === props.question?.subjectProposal?.id);
                let _selectedQuestion = _selectedProposal.questions.find((_q) => _q.id === props.question?.id);
                setAnswers(_selectedQuestion.answers);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                {props.permissions.canEditSubjectProposals && (
                    <div className="my-2">
                        <Button label={translatedMessage('competition.subjectProposal.question.answer.add')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showNewDialog} type={'button'} />
                    </div>
                )}
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {};

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {/*                {(hasPermission('COMPETITION_EDIT') || hasPermission('COMPETITION_VIEW')) && (
                    <Button icon="pi pi-eye" className="p-button-rounded p-button-primary m-1" onClick={() => showAnswerDialog(rowData)} tooltip={translatedMessage('generic.view')} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                )}*/}
                {props.permissions.canEditSubjectProposals && (hasPermission('COMPETITION_EDIT') || hasPermission('COMPETITION_VIEW')) && (
                    <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-info m-1"
                        onClick={() => showEditDialog(rowData)}
                        tooltip={translatedMessage('generic.edit')}
                        tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                        type={'button'}
                    />
                )}
                {props.permissions.canEditSubjectProposals && (
                    <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger m-1"
                        onClick={() => showDeleteDialog(rowData)}
                        disabled={rowData.id < 1000}
                        tooltip={translatedMessage('generic.delete')}
                        tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                        type={'button'}
                    />
                )}
            </div>
        );
    };

    const nameTemplate = (rowData) => {
        if (props.question.questionType === QuestionType.MATCHING_ELEMENTS) {
            return <div className="square">{rowData.name}</div>;
        } else {
            return rowData.name;
        }
    };

    const contentTemplate = (rowData) => {
        return (
            <div className="filed-value">
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rowData.answerContent) }} />
            </div>
        );
    };

    const secondContentTemplate = (rowData) => {
        return (
            <div className="filed-value">
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rowData.secondContent) }} />
            </div>
        );
    };

    const correctAnswerTemplate = (rowData) => {
        return rowData.correctAnswer ? <Button icon="pi pi-check" className="p-button-success p-button-text" type="button" /> : <Button icon="pi pi-times" className="p-button-danger p-button-text" type="button" />;
    };

    const correctOrderTemplate = (rowData) => {
        return <div className="square">{rowData.correctOrder}</div>;
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div>
                    <h5 className="mb-1 font-weight-normal">{translatedMessage('competition.subjectProposal.question.answers')}</h5>
                </div>
                <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                <DataTable
                    ref={dt}
                    value={answers}
                    dataKey="id"
                    paginator
                    rows={DataTableUtils.defalRowsPerPage()}
                    rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                    className="datatable-responsive pcn-datatable"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                    emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                    responsiveLayout="stack"
                    // filterDisplay="row"
                    // filters={filters}
                    // globalFilterFields={['name', 'type', 'active']}
                >
                    <Column
                        field="correctAnswer"
                        body={correctAnswerTemplate}
                        header={translatedMessage('competition.subjectProposal.question.answer.correct')}
                        headerStyle={{ width: '5%', minWidth: '8rem' }}
                        hidden={[QuestionType.MATCHING_ELEMENTS, QuestionType.ORDERING_ELEMENTS, QuestionType.FILL_IN_THE_BLANKS, QuestionType.FREE_TEXT].includes(props.question.questionType)}
                    />
                    <Column
                        field="correctOrder"
                        body={correctOrderTemplate}
                        header={translatedMessage('competition.subjectProposal.question.answer.correctOrder')}
                        headerStyle={{ width: '5%', minWidth: '8rem' }}
                        hidden={![QuestionType.ORDERING_ELEMENTS].includes(props.question.questionType)}
                    />
                    <Column
                        field="name"
                        body={nameTemplate}
                        header={props.question.questionType !== QuestionType.MATCHING_ELEMENTS ? translatedMessage('generic.name') : translatedMessage('competition.subjectProposal.question.answer.number')}
                        sortable
                        headerStyle={{ width: '10%', minWidth: '8rem' }}
                        hidden={[QuestionType.ORDERING_ELEMENTS, QuestionType.FILL_IN_THE_BLANKS, QuestionType.FREE_TEXT].includes(props.question.questionType)}
                    />
                    <Column field="answerContent" body={contentTemplate} header={translatedMessage('competition.subjectProposal.question.answer')} sortable headerStyle={{ width: '30%', minWidth: '10rem' }} />
                    <Column
                        field="correctOrder"
                        body={correctOrderTemplate}
                        header={translatedMessage('competition.subjectProposal.question.answer.correctOrder')}
                        headerStyle={{ width: '5%', minWidth: '8rem' }}
                        hidden={![QuestionType.MATCHING_ELEMENTS].includes(props.question.questionType)}
                    />
                    <Column
                        field="secondContent"
                        body={secondContentTemplate}
                        header={translatedMessage('competition.subjectProposal.question.answer.secondContent')}
                        sortable
                        headerStyle={{ width: '30%', minWidth: '10rem' }}
                        hidden={![QuestionType.MATCHING_ELEMENTS].includes(props.question.questionType)}
                    />
                    <Column body={actionBodyTemplate} />
                </DataTable>

                <AnswerEditDialog value={selectedAnswer} visible={editDialogVisible} visibleSetter={setEditDialogVisible} afterSave={afterSaveQuestionAnswer} question={props.question} permissions={props.permissions} competition={props.competition} />

                {deleteDialogVisible && <DeleteModalComponent visible={deleteDialogVisible} item={selectedAnswer ? selectedAnswer.name : ''} closeDialog={hideDeleteDialog} deleteRecord={() => deleteAnswer()} />}
            </>
        );
    }
};

export default AnswerListComponent;
